import { toggleMark } from 'prosemirror-commands';
import { Mark, Node } from 'prosemirror-model';
import { EditorState, Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import {
  ProsemirrorDispatch,
  RtPMPlugin,
  OverlayPosition,
  EditorButton,
} from '../../../editor/types';
import { MarkService } from '../../../helpers/mark-base';
import { getToolbarPosition } from '../../../helpers/position';
import { TextNode } from '../../basic/nodes';
import { contextMenuCommand } from '../../context-menu/context-menu';
import { LinkMark, LinkSchema } from './link-mark';

export interface LinkAttributes {
  href: string;
  title?: string;
  target?: string;
}

type InsertLinkData = {
  mark: Mark;
  node: Node;
  pos: number;
};

export class PmLinkService<S extends LinkSchema> extends MarkService<S> {
  public setLink(state: EditorState, dispatch: ProsemirrorDispatch, attrs: LinkAttributes) {
    const { $from, $to } = state.selection;
    const hasMark = state.doc.rangeHasMark($from.pos, $to.pos, this.mark);

    if (!hasMark) {
      return toggleMark(this.mark, attrs)(state, dispatch);
    } else {
      const { tr } = state;
      tr.removeMark($from.pos, $to.pos, this.mark);
      tr.addMark($from.pos, $to.pos, this.mark.create(attrs));

      dispatch(tr);
      return;
    }
  }

  public removeLink(state: EditorState<LinkSchema>, dispatch: ProsemirrorDispatch) {
    const { $from, $to } = state.selection;
    const { tr } = state;
    tr.removeMark($from.pos, $to.pos, this.mark);
    dispatch(tr);
    return;
  }
}

const linkPluginKey = new PluginKey('linkPlugin');

enum LinkActions {
  Open = 'open',
  Close = 'close',
}

export const linkCommands = {
  open: (
    state: EditorState<LinkSchema>,
    dispatch: ProsemirrorDispatch,
    tooltipOrigin?: HTMLElement,
  ) => {
    contextMenuCommand.close(state, dispatch, true);
    dispatch(
      state.tr.setMeta(linkPluginKey, {
        type: LinkActions.Open,
        payload: tooltipOrigin,
      }),
    );
  },
  close: (state: EditorState<LinkSchema>, dispatch: ProsemirrorDispatch) => {
    contextMenuCommand.disableForcedState(state, dispatch);
    dispatch(
      state.tr.setMeta(linkPluginKey, {
        type: LinkActions.Close,
      }),
    );
  },
};

type LinkCommands = { type: LinkActions.Open; payload?: HTMLElement } | { type: LinkActions.Close };

export interface LinkInput {
  open: (
    initialValue: string | null,
    position: OverlayPosition,
    setValueCb: (val: string) => void,
    removeCb: () => void,
    exitCb: () => void,
  ) => void;
  close: () => void;
}

export class LinkEditPlugin implements RtPMPlugin {
  public api: {
    linkService: PmLinkService<LinkSchema>;
  };

  public buttons = {
    toggleLink: <EditorButton>{
      icon: 'link',
      isActive: (...args) => this.api.linkService.isActive(...args),
      run: (state, dispatch, eventOrigin, isContextMenu) => {
        const origin = isContextMenu ? undefined : eventOrigin;
        linkCommands.open(state, dispatch, origin);
      },
    },
  };

  private editorView: EditorView<LinkSchema>;

  private plugin = new Plugin({
    key: linkPluginKey,
    filterTransaction: (tr: Transaction, state) => {
      const meta: LinkCommands | undefined = tr.getMeta(linkPluginKey);

      if (meta?.type === LinkActions.Open) {
        const { dispatch, state } = this.editorView;
        let position: OverlayPosition;
        if (meta.payload) {
          const rect = meta.payload.getBoundingClientRect();
          position = {
            top: rect.top,
            left: rect.left + rect.width / 2,
          };
        } else {
          position = getToolbarPosition(this.editorView);
        }

        let linkData: InsertLinkData | null = null;

        state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
          if (linkData) return false;
          if (TextNode.is(node)) {
            const linkMark = node.marks.find(mark => LinkMark.is(mark));
            if (linkMark) {
              linkData = {
                mark: linkMark,
                node,
                pos,
              };
              return false;
            }
          }

          return true;
        });

        const initialValue = linkData ? linkData!.mark.attrs.href : null;

        this.linkInput.open(
          initialValue,
          position,
          (value: string) => this.setLink(value, linkData),
          () => this.api.linkService.removeLink(this.editorView.state, dispatch),
          () => {
            linkCommands.close(this.editorView.state, dispatch);
            this.editorView.focus();
          },
        );
      }

      if (meta?.type === LinkActions.Close) {
        this.linkInput.close();
      }

      return true;
    },
  });

  constructor(private linkInput: LinkInput) {}

  public beforeEditorInit(schema: LinkSchema) {
    this.api = {
      linkService: new PmLinkService(schema.marks.link),
    };
  }

  public afterEditorInit(editorView: EditorView<LinkSchema>) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins() {
    return [this.plugin];
  }

  private setLink(value: string, linkData: InsertLinkData | null) {
    const { state, dispatch } = this.editorView;
    const { selection, schema } = this.editorView.state;
    const linkAttrs = {
      href: value,
      target: '_blank',
    };

    const text = state.doc.textBetween(selection.from, selection.to);
    if (selection.empty || text.trim().length === 0) {
      if (!linkData) {
        const anchor = schema.text(value, [schema.marks.link.create(linkAttrs)]);
        dispatch(state.tr.replaceWith(selection.from, selection.to, [anchor, schema.text(' ')]));
      } else {
        const { tr } = this.editorView.state;
        const from = linkData.pos;
        const to = linkData.pos + linkData.node.text!.length;
        tr.removeMark(from, to, schema.marks.link);
        tr.addMark(from, to, schema.marks.link.create(linkAttrs));
        dispatch(tr);
      }
    } else {
      this.api.linkService.setLink(this.editorView.state, dispatch, linkAttrs);
    }
    this.editorView.focus();
  }
}
